/**
 * One integration (doc 07, screen 4): a band that says how it is doing —
 * health, the last run and its rows, the next run, what waits for a person —
 * then tabs for its runs, rows, questions, settings, history and id links.
 */

import { Link, useParams } from "react-router-dom";
import {
  CalendarClock,
  CalendarDays,
  CircleCheck,
  CircleHelp,
  Globe,
  Hash,
  History,
  Layers,
  Pause,
  Play,
  Rows3,
  Send,
  Zap,
} from "lucide-react";

import { OpenInScout, useIntegrationActions } from "@/components/integrations/IntegrationActions";
import { useCanWrite, useKeyed, useNow } from "@/components/integrations/hooks";
import { AnswersTab } from "@/components/integrations/tabs/AnswersTab";
import { HistoryTab } from "@/components/integrations/tabs/HistoryTab";
import { LinksTab } from "@/components/integrations/tabs/LinksTab";
import { OverviewTab } from "@/components/integrations/tabs/OverviewTab";
import { RowsTab } from "@/components/integrations/tabs/RowsTab";
import { RunView, RunsTab } from "@/components/integrations/tabs/RunsTab";
import { SettingsTab } from "@/components/integrations/tabs/SettingsTab";
import {
  Band,
  Banner,
  Button,
  Card,
  Column,
  Crumbs,
  HealthBadge,
  LinkButton,
  OutcomeBar,
  PageHead,
  SkeletonRows,
  Tabs,
  Tile,
} from "@/components/kit";
import { ApiError } from "@/lib/api/client";
import { listWorkflows } from "@/lib/api/workflows";
import { getIntegration, getIntegrationScoutJob, listRuns } from "@/lib/integrations/api";
import { presetOf, scheduleWords } from "@/lib/integrations/schedule";
import { ago, healthOf, outcomeCounts, plainError, runStatus, sendsTo, when } from "@/lib/integrations/words";

const TABS = ["overview", "runs", "rows", "answers", "settings", "history", "links"] as const;
type Tab = (typeof TABS)[number];

export function IntegrationPage() {
  const { code = "", tab: tabParam, runId } = useParams<{ code: string; tab?: string; runId?: string }>();
  const tab: Tab = runId ? "runs" : ((TABS as readonly string[]).includes(tabParam ?? "") ? (tabParam as Tab) : "overview");
  const now = useNow();
  const canWrite = useCanWrite();
  const detail = useKeyed(`detail|${code}`, () => getIntegration(code));
  const workflows = useKeyed("workflows", () => listWorkflows());
  const item = detail.data;
  const scoutJob = useKeyed(item?.scoutJobId ? `job|${code}|${item.scoutJobId}` : null, () => getIntegrationScoutJob(code));
  // One read of the runs for the band and the Overview, Runs and Rows tabs.
  const runs = useKeyed(item ? `runs|${code}|${item.lastRunAt}` : null, () => listRuns(code, 100));
  const actions = useIntegrationActions(detail.reload);

  if (detail.error && !item) {
    const missing = detail.cause instanceof ApiError && detail.cause.status === 404;
    return (
      <Column>
        <Crumbs trail={[{ label: "Integrations", href: "/integrations" }, { label: code }]} />
        <PageHead title={code} />
        <Banner tone="danger" actions={missing ? <Link to="/integrations">Back to integrations</Link> : <Button onClick={detail.reload}>Try again</Button>}>
          {missing ? `There is no integration called ${code}.` : detail.error}
        </Banner>
      </Column>
    );
  }
  if (!item) {
    return (
      <Column>
        <Crumbs trail={[{ label: "Integrations", href: "/integrations" }, { label: code }]} />
        <PageHead title="Integration" note="Loading…" />
        <Card>
          <SkeletonRows rows={6} columns={4} />
        </Card>
      </Column>
    );
  }

  const health = healthOf(item, { now, scoutJobStatus: scoutJob.data?.status ?? null });
  const workflowName = (workflows.data ?? []).find((row) => row.code === item.workflow)?.name ?? item.workflow;
  const timeZone = item.eventTimezone ?? "UTC";
  const schedule = scoutJob.data?.schedule ?? null;
  const cadence = schedule?.timing ? scheduleWords(presetOf(schedule.timing), schedule.timezone ?? "UTC") : null;
  const answers = item.openAnswers + ((item.needsUpdate ?? []).length ? 1 : 0);
  const href = (key: Tab) => `/integrations/${encodeURIComponent(code)}${key === "overview" ? "" : `/${key}`}`;
  const last = (runs.data ?? []).find((run) => run.via !== "try");
  const parts = last ? outcomeCounts(last.counts) : [];

  const nextValue = !item.scoutJobId
    ? "No Scout job"
    : scoutJob.data
      ? schedule?.next_run_at
        ? when(schedule.next_run_at, timeZone)
        : "When asked"
      : scoutJob.error
        ? "Not known"
        : "…";
  const nextNote = cadence
    ? `${cadence}${schedule?.enabled === false ? " · off in Scout" : ""} · ${timeZone} time`
    : scoutJob.data
      ? "Scout has no schedule for this job"
      : scoutJob.error
        ? "Scout did not describe the job"
        : undefined;

  return (
    <Column>
      <Crumbs
        trail={[
          { label: "Integrations", href: "/integrations" },
          { label: item.eventName, href: `/events/${encodeURIComponent(item.eventCode)}` },
          { label: item.name },
        ]}
      />
      <Band
        title={item.name}
        meta={[
          {
            key: "sends",
            icon: <Send size={14} aria-hidden />,
            content: (
              <span>
                Sends to <Link to={`/workflows/${encodeURIComponent(item.workflow)}`}>{sendsTo(workflowName, item.importCommand)}</Link>
              </span>
            ),
          },
          {
            key: "event",
            icon: <CalendarDays size={14} aria-hidden />,
            content: <Link to={`/events/${encodeURIComponent(item.eventCode)}`}>{item.eventName}</Link>,
          },
          {
            key: "job",
            icon: <Globe size={14} aria-hidden />,
            content: <span>{item.scoutJobId ? `Scout job: ${scoutJob.data?.name ?? item.scoutJobId}` : "No Scout job yet"}</span>,
          },
          {
            key: "version",
            icon: <Layers size={14} aria-hidden />,
            content: (
              <span>
                {item.liveVersion !== null
                  ? `Live version ${item.liveVersion}${item.live?.publishedBy ? `, published by ${item.live.publishedBy}` : ""}${item.live?.publishedAt ? ` ${when(item.live.publishedAt, timeZone)}` : ""}`
                  : "Nothing published yet"}
                {item.draftVersion !== null ? ` · draft ${item.draftVersion} waiting` : ""}
              </span>
            ),
          },
          { key: "code", icon: <Hash size={14} aria-hidden />, content: <span>{item.code}</span> },
        ]}
        actions={
          <>
            {item.state === "setting_up" ? (
              <LinkButton to={`/integrations/new?code=${encodeURIComponent(item.code)}`} tone="primary">
                Continue setting up
              </LinkButton>
            ) : (
              <Button
                onClick={() => actions.ask({ code: item.code, name: item.name, kind: "run" })}
                disabled={!canWrite || item.state === "off" || !item.scoutJobId}
              >
                <Zap size={15} aria-hidden />
                Run now
              </Button>
            )}
            {item.state === "on" && (
              <Button disabled={!canWrite} onClick={() => actions.ask({ code: item.code, name: item.name, kind: "pause" })}>
                <Pause size={15} aria-hidden />
                Pause
              </Button>
            )}
            {(item.state === "paused" || item.state === "paused_by_guard" || item.state === "off") && (
              <Button disabled={!canWrite} onClick={() => actions.ask({ code: item.code, name: item.name, kind: "resume" })}>
                <Play size={15} aria-hidden />
                Resume
              </Button>
            )}
            <OpenInScout jobId={item.scoutJobId} />
          </>
        }
        badge={<HealthBadge health={health} why={health.key === "failing" ? "The last run failed. The reason is just below." : undefined} />}
        tiles={
          <>
            <Tile
              label="Last run"
              icon={<History size={14} aria-hidden />}
              tone={last && runStatus(last.status).tone === "danger" ? "danger" : "neutral"}
              value={last ? ago(last.createdAt, now) : runs.data ? "No runs yet" : "…"}
              note={last ? `${when(last.createdAt, timeZone)} · ${runStatus(last.status).word}` : "Shows once omnium reads a run"}
            />
            <Tile
              label="Rows in last run"
              icon={<Rows3 size={14} aria-hidden />}
              value={last ? last.rowsCollected.toLocaleString() : "—"}
              note={parts.length ? parts.map((part) => `${part.word} ${part.count.toLocaleString()}`).join(" · ") : last ? "No rows" : undefined}
            >
              {last && parts.length > 0 && <OutcomeBar counts={last.counts} small legend={false} />}
            </Tile>
            <Tile label="Next run" icon={<CalendarClock size={14} aria-hidden />} value={nextValue} note={nextNote} />
            <Tile
              label="Waiting for you"
              icon={answers ? <CircleHelp size={14} aria-hidden /> : <CircleCheck size={14} aria-hidden />}
              tone={answers ? "warning" : "good"}
              value={answers}
              note={answers ? "Open Needs answers" : "Nothing waiting"}
              href={href("answers")}
            />
          </>
        }
      />

      {actions.banner}
      {!canWrite && <Banner tone="info">Your account cannot change integrations. You can look at everything.</Banner>}
      {item.state === "setting_up" && tab !== "settings" && (
        <Banner tone="info" title="Not switched on yet.">
          Nothing is written until it is set up and switched on.
        </Banner>
      )}
      {item.state === "paused_by_guard" && (
        <Banner tone="danger" title="Paused by a guard." actions={<Link to={href("answers")}>See what to do</Link>}>
          {item.pauseReason ?? "A safety guard stopped it."}
        </Banner>
      )}
      {item.lastOutcome === "failed" && item.lastError && item.state !== "paused_by_guard" && (
        <Banner tone="danger" title="The last run failed.">
          {plainError(item.lastError)}
        </Banner>
      )}
      {scoutJob.error &&
        (/answered 404/i.test(scoutJob.error) ? (
          <Banner tone="warning" title="Scout does not describe this job." actions={<Button onClick={scoutJob.reload}>Try again</Button>}>
            Scout at {item.scoutBaseUrl} has no job called {item.scoutJobId}, so its schedule and name are not shown. Runs omnium already holds are still here.
          </Banner>
        ) : (
          <Banner tone="warning" title="Scout is not answering." actions={<Button onClick={scoutJob.reload}>Try again</Button>}>
            Settings can still be edited; runs, jobs and previews that need Scout wait.
          </Banner>
        ))}

      <Tabs
        label="Integration"
        current={tab}
        items={[
          { key: "overview", label: "Overview", href: href("overview") },
          { key: "runs", label: "Runs", href: href("runs") },
          { key: "rows", label: "Rows", href: href("rows") },
          { key: "answers", label: "Needs answers", href: href("answers"), count: answers, warn: true },
          { key: "settings", label: "Settings", href: href("settings") },
          { key: "history", label: "History", href: href("history") },
          { key: "links", label: "Id links", href: href("links") },
        ]}
      />

      {tab === "overview" && <OverviewTab item={item} scoutJob={scoutJob.data} scoutError={scoutJob.error} now={now} runs={runs} />}
      {tab === "runs" && !runId && <RunsTab item={item} now={now} runs={runs} />}
      {tab === "runs" && runId && <RunView item={item} runId={runId} canWrite={canWrite} />}
      {tab === "rows" && <RowsTab item={item} runs={runs} />}
      {tab === "answers" && <AnswersTab item={item} canWrite={canWrite} onChanged={detail.reload} />}
      {tab === "settings" && <SettingsTab key={`${item.code}`} item={item} canWrite={canWrite} scoutJob={scoutJob.data} onChanged={detail.reload} />}
      {tab === "history" && <HistoryTab item={item} canWrite={canWrite} onChanged={detail.reload} />}
      {tab === "links" && <LinksTab item={item} canWrite={canWrite} />}
    </Column>
  );
}
